'use client';

import { Map, BarChart3 } from 'lucide-react';
import type { Continent } from '@/hooks/useContinents';

interface ContinentCardProps {
  continent: Continent;
  onCompare: (continent: Continent) => void;
  maxArea: number; // 用于计算面积比例
}

export default function ContinentCard({ continent, onCompare, maxArea }: ContinentCardProps) {
  const formatArea = (area: number) => {
    if (area >= 1000000) {
      return `${(area / 1000000).toFixed(1)}M km²`;
    } else if (area >= 1000) {
      return `${(area / 1000).toFixed(1)}K km²`;
    }
    return `${area} km²`;
  };

  const areaPercentage = (continent.area_km2 / maxArea) * 100;

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4 hover:shadow-md transition-shadow flex flex-col h-full">
      {/* 大洲名称和排名 */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div 
            className="w-4 h-4 rounded-full"
            style={{ backgroundColor: continent.color }}
          />
          <h3 className="font-semibold text-slate-900">{continent.name}</h3>
        </div>
        <div className="flex items-center gap-1 text-xs text-slate-500">
          <BarChart3 className="h-3 w-3" />
          <span>#{continent.area_rank}</span>
        </div>
      </div>

      {/* 多语言名称 */}
      <div className="text-sm text-slate-600 mb-3 flex-1">
        <div className="flex flex-wrap gap-1">
          <span className="bg-slate-100 px-1.5 py-0.5 rounded text-xs">{continent.name_zh}</span>
          <span className="bg-slate-100 px-1.5 py-0.5 rounded text-xs">{continent.name_fr}</span>
          <span className="bg-slate-100 px-1.5 py-0.5 rounded text-xs">{continent.name_es}</span>
          <span className="bg-slate-100 px-1.5 py-0.5 rounded text-xs">{continent.name_ar}</span>
          <span className="bg-slate-100 px-1.5 py-0.5 rounded text-xs">{continent.name_ru}</span>
          <span className="bg-slate-100 px-1.5 py-0.5 rounded text-xs">{continent.name_pt}</span>
        </div>
      </div>

      {/* 面积数据 */}
      <div className="mb-3">
        <div className="flex items-center justify-between mb-1">
          <span className="text-sm text-slate-600">Area</span>
          <span className="text-sm font-medium text-slate-900">{formatArea(continent.area_km2)}</span>
        </div>
        
        {/* 面积比例条 */}
        <div className="w-full bg-slate-200 rounded-full h-2">
          <div 
            className="h-2 rounded-full transition-all duration-300"
            style={{ 
              width: `${areaPercentage}%`,
              backgroundColor: continent.color
            }}
          />
        </div>
        <div className="text-xs text-slate-500 mt-1">
          {areaPercentage.toFixed(1)}% of largest
        </div>
      </div>

      {/* 国家数量 */}
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm text-slate-600">Countries</span>
        <span className="text-sm font-medium text-slate-900">{continent.countryCount}</span>
      </div>

      {/* 操作按钮 - 固定在底部 */}
      <div className="mt-auto">
        <button
          onClick={() => onCompare(continent)}
          className="w-full bg-sky-700 text-white py-2 px-3 rounded-md text-sm font-medium hover:bg-sky-800 transition-colors flex items-center justify-center gap-2"
        >
          <Map className="h-4 w-4" />
          Explore on Map
        </button>
      </div>
    </div>
  );
}
